// WWI war memorial — a stone obelisk on a stepped plinth, a bronze-green
// wreath ring and plaque on the face, poppy-red tributes laid at the base and
// two low flanking posts. Single-variant scenery, not a staged building.
import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

const { box, cyl } = styleV3;

const STONE = "#8d8a85";
const STONE_LT = "#a09d97";
const STONE_DK = "#6e6b66";
const BRONZE = "#5a6e4a"; // weathered bronze-green
const POPPY = "#b0342e";

// The bronze wreath — a ring of small spheres laid on the obelisk's front face.
function wreath(): Part[] {
  const out: Part[] = [];
  const n = 8, r = 0.085, cy = 0.82;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    out.push({
      geometry: new THREE.SphereGeometry(0.028, 6, 5), color: BRONZE,
      position: [Math.cos(a) * r, cy + Math.sin(a) * r, 0.08],
    });
  }
  return out;
}

// Poppy tributes laid on the plinth steps.
function poppies(): Part[] {
  const spots: [number, number, number][] = [
    [-0.19, 0.32, 0.15], [0.16, 0.32, 0.18], [-0.07, 0.2, 0.25], [0.23, 0.2, 0.22],
  ];
  return spots.map(([x, y, z]) => ({
    geometry: new THREE.IcosahedronGeometry(0.032, 0), color: POPPY,
    position: [x, y, z] as [number, number, number],
  }));
}

export const warMemorial = defineModel({
  id: "war-memorial",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "War Memorial",
  tags: ["scenery", "monument", "wwi", "military", "memorial"],
  summary: "A stone obelisk on a stepped plinth with a bronze wreath and poppy tributes.",
  description:
    "A Great-War memorial: a tapering stone obelisk on a three-step plinth, " +
    "a bronze-green wreath ring and plaque on its face, poppy-red tributes " +
    "laid on the steps and two low flanking posts.",
  author: "Fable 5",
  footprint: 0.4,
  parts: (): Part[] => [
    // The stepped plinth.
    box(0.72, 0.1, 0.72, STONE_DK, [0, 0.05, 0]),
    box(0.56, 0.1, 0.56, STONE, [0, 0.15, 0]),
    box(0.4, 0.12, 0.4, STONE_LT, [0, 0.26, 0]),
    // The tapering obelisk shaft and its pyramid cap.
    { geometry: new THREE.CylinderGeometry(0.085, 0.14, 0.85, 4), color: STONE,
      position: [0, 0.745, 0], rotation: [0, Math.PI / 4, 0] },
    { geometry: new THREE.ConeGeometry(0.11, 0.16, 4), color: STONE_LT,
      position: [0, 1.25, 0], rotation: [0, Math.PI / 4, 0] },
    // The bronze plaque under the wreath.
    box(0.16, 0.2, 0.02, BRONZE, [0, 0.5, 0.095]),
    ...wreath(),
    ...poppies(),
    // Two low flanking posts at the front corners.
    cyl(0.03, 0.035, 0.18, 5, STONE_DK, [-0.34, 0.09, 0.34]),
    cyl(0.03, 0.035, 0.18, 5, STONE_DK, [0.34, 0.09, 0.34]),
  ],
  animations: [],
});
